'use client'

import { useEffect, useState } from 'react'
import {
  ArrowLeft,
  Camera,
  Image as ImageIcon,
  Loader2,
  MessageSquare,
  ShieldCheck,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ContentPage } from '@/components/content-page'

type GalleryImage = {
  id: number
  url: string
  topicId: number
  topicTitle: string
  authorHandle: string
  createdAt: string
}

function formatDate(value: string) {
  const date = new Date(value.includes('T') ? value : `${value.replace(' ', 'T')}Z`)
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

export default function ForumGalleryPage() {
  const [images, setImages] = useState<GalleryImage[]>([])
  const [message, setMessage] = useState('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    fetch('/api/forum/gallery')
      .then(async (response) => {
        const data = (await response.json().catch(() => ({}))) as {
          images?: GalleryImage[]
          error?: string
        }
        if (!response.ok) throw new Error(data.error || 'Could not load the gallery.')
        if (!cancelled) setImages(data.images || [])
      })
      .catch((error) => {
        if (!cancelled) {
          setMessage(error instanceof Error ? error.message : 'Could not load the gallery.')
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  return (
    <ContentPage
      wide
      eyebrow="// forums / show and tell"
      title="Community gallery"
      description="Radio shacks, mobile installs, antennas, field operations, and projects shared by members."
    >
      <a
        href="/forum"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to forums
      </a>

      <section className="mt-5 grid gap-4 rounded-2xl border border-border bg-card p-5 sm:grid-cols-[minmax(0,1fr)_minmax(15rem,0.7fr)]">
        <div>
          <h2 className="font-semibold text-foreground">A visual index of the community</h2>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            The gallery collects approved photos attached to discussions in the Show and tell
            category. It is a quick way to browse real radio setups, then jump into the original
            thread for the details and conversation.
          </p>
          <ul className="mt-3 grid gap-2 text-sm text-muted-foreground sm:grid-cols-2">
            <li className="flex items-start gap-2">
              <ImageIcon className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              Only approved images from Show and tell topics and replies appear here.
            </li>
            <li className="flex items-start gap-2">
              <MessageSquare className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              Select any photo to open its topic and join the discussion.
            </li>
          </ul>
        </div>
        <div className="rounded-xl border border-primary/20 bg-primary/5 p-4">
          <p className="flex items-center gap-2 text-sm font-medium text-foreground">
            <Camera className="h-4 w-4 text-primary" />
            Share your setup
          </p>
          <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
            Start a New topic, choose <strong className="text-foreground">Show and tell</strong>,
            and attach your photo. A moderator reviews it before it is published.
          </p>
          <p className="mt-3 flex items-start gap-2 text-xs leading-relaxed text-muted-foreground">
            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
            Review keeps private, explicit, or inappropriate images out of the public forum.
          </p>
        </div>
      </section>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : message ? (
        <div className="mt-6 rounded-2xl border border-border bg-card p-8 text-center">
          <ImageIcon className="mx-auto h-7 w-7 text-muted-foreground" />
          <p className="mt-3 text-sm text-muted-foreground">{message}</p>
          {message.includes('log in') && (
            <Button className="mt-4" nativeButton={false} render={<a href="/login" />}>
              Log in
            </Button>
          )}
        </div>
      ) : images.length === 0 ? (
        <div className="mt-6 rounded-2xl border border-dashed border-border bg-card/50 p-12 text-center">
          <ImageIcon className="mx-auto h-7 w-7 text-muted-foreground" />
          <p className="mt-3 text-sm text-muted-foreground">
            No approved show-and-tell images yet.
          </p>
          <Button className="mt-4" nativeButton={false} render={<a href="/forum/new" />}>
            Share your setup
          </Button>
        </div>
      ) : (
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {images.map((image) => (
            <a
              key={image.id}
              href={`/forum/topic/${image.topicId}`}
              className="group overflow-hidden rounded-xl border border-border bg-card transition-colors hover:border-primary/40"
            >
              <img
                src={image.url}
                alt={image.topicTitle}
                loading="lazy"
                className="aspect-[4/3] w-full bg-background object-cover transition-transform duration-300 group-hover:scale-[1.02]"
              />
              <div className="p-4">
                <h2 className="font-semibold group-hover:text-primary">{image.topicTitle}</h2>
                <p className="mt-1 text-xs text-muted-foreground">
                  {image.authorHandle} · {formatDate(image.createdAt)}
                </p>
              </div>
            </a>
          ))}
        </div>
      )}
    </ContentPage>
  )
}
